/**
 * Markdown Renderer for CLI
 * Beautiful terminal markdown rendering using marked + marked-terminal
 */

import { Marked } from 'marked';
import { markedTerminal } from 'marked-terminal';
import chalk from 'chalk';
import {
    primary,
    primaryBold,
    success,
    error,
    muted,
    code as codeStyle,
    link,
    symbols,
} from '../ui/theme/index.js';

// Configure marked with terminal renderer
// Use synchronous parsing by setting async: false
const marked = new Marked();
marked.use(
    markedTerminal({
        // Code blocks
        code: codeStyle,
        blockquote: muted.italic,
        // Headers
        heading: primary.bold,
        firstHeading: primaryBold,
        // Text styles - use explicit chalk functions for consistent rendering
        strong: (text: string) => chalk.bold(text),
        em: (text: string) => chalk.italic(text),
        del: (text: string) => chalk.strikethrough.gray(text),
        // Links and references
        link: link,
        href: link,
        // Lists
        listitem: chalk.reset,
        // Code highlighting - inline code
        codespan: (text: string) => chalk.yellow(text),
        // Paragraph - ensure proper rendering
        paragraph: (text: string) => text,
        // Misc
        unescape: true,
        emoji: true,
        width: process.stdout.columns || 80,
        showSectionPrefix: false,
        reflowText: true,
        tab: 2,
    })
);

/**
 * Render markdown text to styled terminal output
 * Uses synchronous parsing to ensure consistent results
 */
export function renderMarkdown(text: string): string {
    if (!text || typeof text !== 'string') {
        return text || '';
    }

    try {
        // marked.parse can return string | Promise<string>
        // With async: false, it should always return a string synchronously
        const result = marked.parse(text, { async: false });

        if (typeof result === 'string') {
            return result;
        }

        // Fallback: if somehow we got a promise, return original text
        console.warn('[renderMarkdown] Unexpected async result');
        return text;
    } catch (err) {
        // Fallback to plain text on error
        if (process.env.DEBUG) {
            console.error('[renderMarkdown] Error:', err);
        }
        return text;
    }
}

/**
 * Render an error message
 */
export function renderError(message: string): string {
    return error(`\n${symbols.error} Error: ${message}\n`);
}

/**
 * Render a success message
 */
export function renderSuccess(message: string): string {
    return success(`${symbols.success} ${message}`);
}

/**
 * Get current terminal width
 */
export function getTerminalWidth(): number {
    return process.stdout.columns || 80;
}

/**
 * Render sparklet created indicator
 */
export function renderSparkletCreated(hashKey: string, title: string): string {
    return success(`\n✨ Sparklet created: ${chalk.bold(title)} (${hashKey.slice(0, 8)}...)\n`);
}

/**
 * Render media generated indicator
 */
export function renderMediaGenerated(
    assetType: 'image' | 'video',
    fileName?: string,
    signedUrl?: string
): string {
    const typeLabel = assetType === 'image' ? 'Image' : 'Video';
    const fileInfo = fileName ? ` (${fileName})` : '';
    const urlInfo = signedUrl ? `\n  ${link(signedUrl)}` : '';
    return success(`\n🎨 ${typeLabel} generated${fileInfo}${urlInfo}\n`);
}
